import { ListStore, ListStoreFilterModel } from '../../../../../stores/base/ListStore';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import {
  GridCallbackDetails,
  GridEnrichedColDef,
  GridFilterModel,
  GridRowClassNameParams,
  GridSelectionModel,
  GridSortItem,
  GridSortModel
} from '@mui/x-data-grid-pro';
import { RowViewModel } from './row';
import { TableViewModel } from './table';
import { LoadingViewModel } from '../../../../base/viewModels';
import { Entity } from '../../../../base/viewModels';

export type ManagedTableGridColDef = GridEnrichedColDef & {
  getOrderingString?: (desc: boolean) => string;
};

export abstract class TableManagerViewModel<
  TEntity extends Entity<unknown>,
  TRowViewModel extends RowViewModel<TEntity, TTableViewModel>,
  TTableViewModel extends TableViewModel<TEntity, TRowViewModel>,
  TBackendFilterModel extends object = object
> {
  isAllSelected = false;

  constructor(
    public tableViewModel: TTableViewModel,
    public listStore: ListStore<TEntity, TBackendFilterModel, object, object>,
    public cacheSelectionData = true
  ) {
    reaction(
      () => this.listStore.data,
      (data) => {
        if (data) {
          tableViewModel.setData(data);
        }
      }
    );

    reaction(
      () => this.listStore.loading,
      (loading) => {
        loading ? this.loading.start() : this.loading.stop();
      }
    );

    if (this.cacheSelectionData) {
      reaction(
        () => this.selectionModel.length,
        () => {
          this.updateSelectedItems(this.selectionModel);
        }
      );
    }

    makeObservable(this, {
      totalCount: computed,
      pageSize: computed,
      page: computed,
      selectionModel: observable,
      selectedItems: observable,
      onSelectionModelChange: action,
      clear: action,
      clearSelection: action,
      refresh: action
    });
  }

  getFilterString(model: GridFilterModel, field: string) {
    return model.items.find((i) => i.columnField == field)?.value;
  }

  applyFilter(model: GridFilterModel, details?: GridCallbackDetails<'filter'>) {
    this.listStore.setFilter(this.mapMuiFilterModel(model, details));
    this.listStore.getPage();
  }

  abstract mapMuiFilterModel(
    model: GridFilterModel,
    details?: GridCallbackDetails<'filter'>
  ): ListStoreFilterModel<TBackendFilterModel, TEntity>;

  filterDelay = 0;

  get totalCount() {
    return this.listStore.totalCount;
  }

  get page() {
    return this.listStore.page;
  }

  get pageSize() {
    return this.listStore.pageSize;
  }

  loading = new LoadingViewModel(false);

  onPageSizeChange(size: number) {
    this.listStore.setPageSize(size);
  }

  onPageChange(page: number) {
    this.listStore.getPage(page);
  }

  private getSortingString = (item: GridSortItem) => {
    const column = this.tableViewModel.apiRef?.current.getColumn(
      item.field
    ) as ManagedTableGridColDef;
    const desc = item.sort == 'desc';

    if (column.getOrderingString) {
      return column.getOrderingString(desc);
    } else {
      return `${desc ? '-' : ''}${item.field}`;
    }
  };

  onSortModelChange(model: GridSortModel) {
    this.listStore.setOrder(model.map((item) => this.getSortingString(item)));
    this.listStore.getPage();
  }

  delayedUpdate?: NodeJS.Timeout;

  protected filterModelChanged() {
    this.tableApiRef?.upsertFilterItem({ id: 'fake', columnField: 'none' });
  }

  async onFilterModelChange(model: GridFilterModel, details: GridCallbackDetails<'filter'>) {
    if (this.filterDelay > 0) {
      if (this.delayedUpdate) {
        clearTimeout(this.delayedUpdate);
      }
      this.delayedUpdate = setTimeout(() => {
        this.applyFilter(model, details);
      }, this.filterDelay);
    } else {
      this.applyFilter(model, details);
    }
    model.items = model.items.filter((i) => i.id != 'fake');
    return model;
  }

  selectionModel: GridSelectionModel = [];

  protected async onSelectedAll() {
    const currentPage = this.page;
    const currentPageSize = this.pageSize;

    const pages = Math.ceil(this.totalCount / 100);

    // first page
    await this.listStore.setPageSize(100);
    await this.listStore.getPage(0);
    runInAction(() => {
      this.isAllSelected = true;
      this.selectionModel = (this.listStore.data?.map((e) => e.id) ?? []) as GridSelectionModel;
    });

    // other pages
    for (let page = 1; page < pages; page++) {
      await this.listStore.getPage(page);
      runInAction(() => {
        this.selectionModel.push(
          ...((this.listStore.data?.map((e) => e.id) ?? []) as GridSelectionModel)
        );
      });
    }

    this.listStore.setPageSize(currentPageSize);
    this.listStore.getPage(currentPage);
  }

  protected onDeselectAll() {
    this.selectionModel = [];
  }

  protected isSelectAll(newLength: number) {
    return newLength == this.pageSize && this.selectionModel.length == 0;
  }

  protected isDeselectedAll(newLength: number) {
    const threshold = this.totalCount - this.pageSize;
    return newLength == threshold && this.selectionModel.length > threshold + 1;
  }

  selectedItems: TEntity[] = [];

  protected updateSelectedItems(selectionModel: GridSelectionModel) {
    const newIds = selectionModel.filter(
      (itemId) => this.selectedItems.find((i) => i.id === itemId) == undefined
    ) as TEntity['id'][];

    const newItems = this.tableViewModel.data.filter((i) => newIds.includes(i.id)) ?? [];

    this.selectedItems = this.selectedItems
      .filter((i) => selectionModel.includes(i.id as string))
      .concat(newItems);
  }

  onSelectionModelChange(model: GridSelectionModel) {
    if (this.isSelectAll(model.length)) {
      this.onSelectedAll();
    } else {
      this.isAllSelected = false;
      if (this.isDeselectedAll(model.length)) {
        this.onDeselectAll();
      } else {
        this.selectionModel = model;
      }
    }
  }

  clear() {
    this.listStore.clear();
    this.clearSelection();
    this.selectedItems = [];
  }

  clearSelection() {
    this.selectionModel = [];
  }

  async refresh(hard = false) {
    if (hard) return this.listStore.refresh();
    return this.listStore.getPage();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getRowClassName(params: GridRowClassNameParams<TRowViewModel>) {
    return '';
  }

  public get customTableRoofHeadline(): string | undefined {
    return undefined;
  }

  protected get tableApiRef() {
    return this.tableViewModel.apiRef?.current;
  }
}
